import React, { useMemo, useState } from 'react';
import { 
  IoBook, 
  IoVideocam, 
  IoChatbubbles, 
  IoCash, 
  IoCalendar, 
  IoSchool,
  IoCheckmarkCircle,
  IoDocumentText,
  IoPeople,
  IoTrendingUp,
  IoLibrary,
  IoGlobe
} from 'react-icons/io5';
import { User } from '@/types';
import './Resources.css';

interface ResourcesProps {
  user?: User | null;
}

interface Resource {
  id: number;
  icon: React.ComponentType<any>;
  title: string;
  description: string;
  link: string;
  linkText: string;
  category: string;
  bgColor: string;
  iconColor: string;
  isNew?: boolean;
  isPopular?: boolean;
}

const Resources: React.FC<ResourcesProps> = ({ user }) => {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  const resources: Resource[] = useMemo(() => [
    {
      id: 1,
      icon: IoBook,
      title: 'Essay Writing Guide',
      description: 'Comprehensive guide to writing compelling college essays with examples and tips from admissions experts.',
      link: '#',
      linkText: 'Read Guide →',
      category: 'writing',
      bgColor: 'bg-blue-100',
      iconColor: 'text-blue-600',
      isPopular: true,
    },
    {
      id: 2,
      icon: IoVideocam,
      title: 'Video Tutorials',
      description: 'Step-by-step video guides for completing each section of your application successfully.',
      link: '#',
      linkText: 'Watch Now →',
      category: 'tutorials',
      bgColor: 'bg-green-100',
      iconColor: 'text-green-600',
      isNew: true,
    },
    {
      id: 3,
      icon: IoChatbubbles,
      title: 'Community Forum',
      description: 'Connect with other students and share advice on the college application process.',
      link: '#',
      linkText: 'Join Discussion →',
      category: 'community',
      bgColor: 'bg-purple-100',
      iconColor: 'text-purple-600',
    },
    {
      id: 4,
      icon: IoCash,
      title: 'Financial Aid Calculator',
      description: 'Estimate your financial aid eligibility and discover scholarship opportunities.',
      link: '#',
      linkText: 'Calculate Aid →',
      category: 'financial',
      bgColor: 'bg-yellow-100',
      iconColor: 'text-yellow-600',
      isPopular: true,
    },
    {
      id: 5,
      icon: IoCalendar,
      title: 'Deadline Tracker',
      description: 'Never miss an important deadline with our automated reminder system and calendar sync.',
      link: '/deadlines',
      linkText: 'View Deadlines →',
      category: 'planning',
      bgColor: 'bg-red-100',
      iconColor: 'text-red-600',
    },
    {
      id: 6,
      icon: IoSchool,
      title: 'College Search Tool',
      description: 'Research and compare colleges, admission rates, and find your best fit schools.',
      link: '/college-list-builder',
      linkText: 'Search Colleges →',
      category: 'research',
      bgColor: 'bg-indigo-100',
      iconColor: 'text-indigo-600',
    },
    {
      id: 7,
      icon: IoCheckmarkCircle,
      title: 'Application Checklist',
      description: 'Track your progress with our comprehensive application completion checklist.',
      link: '#',
      linkText: 'View Checklist →',
      category: 'planning',
      bgColor: 'bg-emerald-100',
      iconColor: 'text-emerald-600',
    },
    {
      id: 8,
      icon: IoDocumentText,
      title: 'Resume Builder',
      description: 'Create a professional resume tailored for college applications and scholarships.',
      link: '#',
      linkText: 'Build Resume →',
      category: 'writing',
      bgColor: 'bg-orange-100',
      iconColor: 'text-orange-600',
      isNew: true,
    },
    {
      id: 9,
      icon: IoPeople,
      title: 'Mentorship Program',
      description: 'Get paired with college students and alumni for personalized guidance.',
      link: '#',
      linkText: 'Find Mentor →',
      category: 'community',
      bgColor: 'bg-pink-100',
      iconColor: 'text-pink-600',
    },
    {
      id: 10,
      icon: IoTrendingUp,
      title: 'Admission Statistics',
      description: 'Access real-time data on college admission rates and trends.',
      link: '#',
      linkText: 'View Stats →',
      category: 'research',
      bgColor: 'bg-cyan-100',
      iconColor: 'text-cyan-600',
    },
    {
      id: 11,
      icon: IoLibrary,
      title: 'Resource Library',
      description: 'Browse our extensive collection of guides, templates, and examples.',
      link: '#',
      linkText: 'Browse Library →',
      category: 'writing',
      bgColor: 'bg-violet-100',
      iconColor: 'text-violet-600',
    },
    {
      id: 12,
      icon: IoGlobe,
      title: 'International Students Guide',
      description: 'Specialized resources for international students applying to US colleges.',
      link: '#',
      linkText: 'Learn More →',
      category: 'tutorials',
      bgColor: 'bg-teal-100',
      iconColor: 'text-teal-600',
    },
  ], []);

  const categories = useMemo(() => [
    { key: 'all', label: 'All Resources', count: resources.length },
    { key: 'writing', label: 'Writing & Essays', count: resources.filter(r => r.category === 'writing').length },
    { key: 'research', label: 'College Research', count: resources.filter(r => r.category === 'research').length },
    { key: 'financial', label: 'Financial Aid', count: resources.filter(r => r.category === 'financial').length },
    { key: 'planning', label: 'Planning & Organization', count: resources.filter(r => r.category === 'planning').length },
    { key: 'community', label: 'Community & Support', count: resources.filter(r => r.category === 'community').length },
    { key: 'tutorials', label: 'Tutorials & Guides', count: resources.filter(r => r.category === 'tutorials').length },
  ], [resources]);

  const filteredResources = useMemo(() => {
    if (selectedCategory === 'all') return resources;
    return resources.filter(resource => resource.category === selectedCategory);
  }, [resources, selectedCategory]);

  const handleResourceClick = (resource: Resource) => {
    if (resource.link !== '#') {
      if (resource.link.startsWith('/')) {
        // Internal navigation
        window.location.href = resource.link;
      } else {
        // External link
        window.open(resource.link, '_blank');
      }
    }
  };

  return (
    <div className="resources-page">
      <div className="resources-container">
        {/* Header Section */}
        <div className="resources-header-section">
          <h1 className="resources-main-title">Resources & Tools</h1>
          <p className="resources-subtitle">
            Everything you need to succeed in your college application journey
          </p>
        </div>

        {/* Category Filter */}
        <div className="resources-category-filter">
          <div className="category-tabs">
            {categories.map((category) => (
              <button
                key={category.key}
                className={`category-tab ${selectedCategory === category.key ? 'active' : ''}`}
                onClick={() => setSelectedCategory(category.key)}
              >
                {category.label}
                <span className="category-count">({category.count})</span>
              </button>
            ))}
          </div>
        </div>

        {/* Resources Grid */}
        <div className="resources-section">
          <div className="resources-grid">
            {filteredResources.map((resource) => {
              const IconComponent = resource.icon;
              return (
                <div 
                  key={resource.id} 
                  className="resource-card"
                  onClick={() => handleResourceClick(resource)}
                >
                  {(resource.isNew || resource.isPopular) && (
                    <div className="resource-badge">
                      {resource.isNew && <span className="badge-new">New</span>}
                      {resource.isPopular && <span className="badge-popular">Popular</span>}
                    </div>
                  )}
                  
                  <div className={`resource-icon-container ${resource.bgColor}`}>
                    <IconComponent className={`resource-icon ${resource.iconColor}`} />
                  </div>
                  
                  <h3 className="resource-title">{resource.title}</h3>
                  <p className="resource-description">{resource.description}</p>
                  
                  <a 
                    href={resource.link} 
                    className="resource-link"
                    onClick={(e) => {
                      e.preventDefault();
                      handleResourceClick(resource);
                    }}
                  >
                    {resource.linkText}
                  </a>
                </div>
              );
            })}
          </div>

          {filteredResources.length === 0 && (
            <div className="empty-state">
              <IoLibrary className="empty-icon" />
              <h3>No resources found</h3>
              <p>Try selecting a different category to see more resources.</p>
            </div>
          )}
        </div>

        {/* Help Section */}
        <div className="resources-help-section">
          <div className="help-card">
            <h3 className="help-title">Need More Help?</h3>
            <p className="help-description">
              Can't find what you're looking for? Our support team is here to help you succeed.
            </p>
            <div className="help-actions">
              <button className="help-button primary">Contact Support</button>
              <button className="help-button secondary">Request Resource</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Resources;